/**
 * Database-Driven Project Cache Hook
 * 
 * 🎯 PURPOSE: Replaces fragmented React Query cache population with reliable database calls
 * 
 * ARCHITECTURE:
 * - Initial population on session start
 * - Periodic refresh every 5 minutes  
 * - Single source of truth from database
 * - Preserves all real-time mutation updates
 * 
 * INTEGRATION:
 * - Call once in app initialization (not on every page)
 * - Runs independently of React Query cache state
 * - Non-blocking, error-safe operations
 * - Maintains existing mutation sync system
 */

import { useEffect, useRef, useCallback } from 'react';
import { useUser } from '@/hooks/auth/use-user';
import { supabase } from '@/integrations/supabase/client';
import { saveProjectIndex, getProjectIndex } from '@/utils/project-navigation-cache';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY = 1000; // 1 second delay on app start

// Singleton pattern to prevent multiple initializations
let activeInstance: string | null = null;
const generateInstanceId = () => Math.random().toString(36).substring(7);

/**
 * Database-driven cache population hook
 * Replaces the 4 fragmented cache population hooks with a single reliable system
 */
export const useProjectsPagesDatabaseCache = () => {
  const { user } = useUser();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number>(0);
  const isActiveRef = useRef(true);
  const instanceIdRef = useRef<string | null>(null);

  /**
   * Fetch complete user context from database using our PostgreSQL function
   */
  const fetchUserContext = useCallback(async (): Promise<void> => {
    if (!user?.id || !isActiveRef.current) return;

    try {
      console.log('[DatabaseCache] Fetching user context from database...');
      
      // Call our PostgreSQL function directly (TypeScript types not yet updated)
      const { data, error } = await supabase.rpc('get_user_projects_pages_context' as any, {
        p_user_id: user.id
      });

      if (error) {
        console.error('[DatabaseCache] Database error:', error);
        return;
      }

      if (!data) {
        console.warn('[DatabaseCache] No data returned from function');
        return;
      }

      // Type the response data properly
      const responseData = data as any;

      // Check if data has an error field (from function exception handling)
      if (responseData.error) {
        console.error('[DatabaseCache] Function returned error:', responseData.error);
        return;
      }

      console.log('[DatabaseCache] Successfully fetched context:', {
        projects: responseData.stats?.projectCount || 0,
        pages: responseData.stats?.pageCount || 0,
        version: responseData.version
      });

      // Transform database response to match our cache structure
      // Pages are now properly nested within each project
      const cacheData = {
        projects: responseData.projects || [],
        creationContext: responseData.creationContext || {
          hasProjects: false,
          defaultProjectId: null,
          totalProjects: 0,
          totalPages: 0
        }
      };

      // Save to cache (this will trigger reactive updates)
      saveProjectIndex(cacheData, user.id);
      lastFetchRef.current = Date.now();

    } catch (error) {
      console.error('[DatabaseCache] Error fetching user context:', error);
      // Don't throw - this is a background operation
    }
  }, [user?.id]);

  /**
   * Check if cache needs refresh based on timing
   */
  const needsRefresh = useCallback((): boolean => {
    if (!user?.id) return false;

    const cached = getProjectIndex();
    
    // No cache exists - needs initial population
    if (!cached) return true;
    
    // Different user - clear and refresh
    if (cached.userId !== user.id) return true;
    
    // Check if enough time has passed since last fetch
    const timeSinceLastFetch = Date.now() - lastFetchRef.current;
    return timeSinceLastFetch >= REFRESH_INTERVAL;
  }, [user?.id]);

  /**
   * Smart refresh that only fetches when needed
   */
  const smartRefresh = useCallback(async (): Promise<void> => {
    if (!needsRefresh()) {
      console.log('[DatabaseCache] Skipping refresh - cache is current');
      return;
    }

    await fetchUserContext();
  }, [needsRefresh, fetchUserContext]);

  /**
   * Set up periodic refresh interval
   */
  const setupInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (isActiveRef.current && user?.id) {
        smartRefresh();
      }
    }, REFRESH_INTERVAL);

    console.log('[DatabaseCache] Periodic refresh interval established');
  }, [smartRefresh, user?.id]);

  /**
   * Initialize cache on user login
   */
  useEffect(() => {
    if (!user?.id) {
      // Clear any existing interval when user logs out
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Release singleton lock when logging out
      if (instanceIdRef.current === activeInstance) {
        activeInstance = null;
      }
      return;
    }

    // Singleton check - prevent multiple instances from running
    if (activeInstance && activeInstance !== instanceIdRef.current) {
      console.log('[DatabaseCache] Another instance is already active, skipping initialization');
      return;
    }

    // Claim singleton lock
    if (!instanceIdRef.current) {
      instanceIdRef.current = generateInstanceId();
    }
    activeInstance = instanceIdRef.current;

    console.log('[DatabaseCache] Initializing for user:', user.id, 'Instance:', instanceIdRef.current);
    isActiveRef.current = true;

    // Initial fetch with small delay to let app stabilize
    const initialTimer = setTimeout(() => {
      if (isActiveRef.current && user?.id) {
        smartRefresh();
      }
    }, INITIAL_DELAY);

    // Set up periodic refresh
    setupInterval();

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Release singleton lock on unmount
      if (instanceIdRef.current === activeInstance) {
        activeInstance = null;
      }
    };
  }, [user?.id, smartRefresh, setupInterval]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  /**
   * Manual refresh function for error recovery
   */
  const manualRefresh = useCallback(async (): Promise<void> => {
    lastFetchRef.current = 0; // Force refresh
    await smartRefresh();
  }, [smartRefresh]);

  return {
    manualRefresh,
    isActive: isActiveRef.current && !!user?.id
  };
};

/**
 * Lightweight hook for manual cache refresh
 * Use this when you need to trigger cache refresh from other components
 */
export const useManualCacheRefresh = () => {
  const { manualRefresh } = useProjectsPagesDatabaseCache();
  
  return {
    refreshCache: manualRefresh
  };
}; 